// ---------------------------------------------------------------------------
// V33 — THE DELIVERY SCORECARD.
//
// ★ EVERY METRIC ON THIS SCREEN SHOWS BOTH OF ITS SOURCES AND WHETHER THEY
//   AGREE, and that is the screen rather than an ornament on it.
//
// The roadmap's verify line is that every metric reconciles against the
// underlying audit events, so no metric may be computed from a stored counter
// that could drift. A screen that showed one figure per metric would be exactly
// the thing that line forbids, one layer up: a number with no provenance is a
// number that has already started drifting and has not told anybody.
//
// So a fill rate is drawn as the live count, the count folded out of the
// transition journal, and a chip saying whether they match. Two of the five
// metrics have NO live counterpart at all — nothing in `app.assignment` records
// when a seat was accepted, or that a row was ever a reserve — and they say so
// in words rather than showing a blank where a second figure should be.
//
// NO ARITHMETIC IN THIS FILE. Every rate arrives as basis points and `percent`
// from `system/format.ts` renders it.
//
// STATUS IS NEVER COLOUR ALONE. Every chip carries a word, because this is read
// by somebody deciding whether to trust a number.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";

import {
  analytics as analyticsApi,
  type ClientDelivery,
  type CounterDefect,
  type CounterDriftRow,
  type CounterRegisterRow,
  type ReconciliationCheck,
  type ScorecardMetric,
} from "../api/analytics";
import { send } from "../api/auth";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { download } from "../system/download";
import { csv, monthLabel, percent } from "../system/format";

type ScorecardReply = { metrics?: ScorecardMetric[]; error?: { message: string } };
type ClientsReply = { clients?: ClientDelivery[]; error?: { message: string } };
type ReconciliationReply = {
  checks?: ReconciliationCheck[];
  reconciled?: boolean;
  error?: { message: string };
};
type CountersReply = {
  register?: CounterRegisterRow[];
  drift?: CounterDriftRow[];
  defects?: CounterDefect[];
  error?: { message: string };
};

// THE DEFAULT WINDOW IS THE LAST YEAR, AND IT IS THE CALLER'S CHOICE.
// The API refuses a request with no period rather than assuming one, so the
// default lives here where a person can see it and change it, not in the
// database where a scorecard would silently answer for a period nobody picked.
//
// ★ AND "A YEAR AGO" IS A CALENDAR OPERATION, NEVER A SUBTRACTION. The first
// draft subtracted a constant number of milliseconds from the current instant,
// and `verify-V12.sh` failed it from twenty-one slices away, correctly: 365 days
// is not a year across a leap year, and a fixed span of milliseconds is not a
// year across a daylight-saving boundary either. `setUTCFullYear` asks the
// calendar.
//
// The expression itself is NOT REPEATED HERE. That guard is not
// comment-stripped, so a note explaining the fix would fail the check the fix
// was made to satisfy — which is what happened, on the run after the fix.
function defaultWindow(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setUTCFullYear(from.getUTCFullYear() - 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** A figure in its unit, printed as the unit's row says (S70, `app.metric_unit`):
 *  a percentage from basis points, or the number and the unit's word. This screen
 *  knows no unit by name. */
function figure(
  value: string | null,
  m: { unit_is_percent: boolean; unit_suffix: string | null },
  tl: (text: string) => string,
): string {
  if (value === null) return "—";
  if (m.unit_is_percent) return percent(Number.parseInt(value, 10));
  return m.unit_suffix ? `${value} ${tl(m.unit_suffix)}` : value;
}

/** The by-client columns are basis points by their field's name, not by a unit id. */
function basisPoints(value: string | null): string {
  return value === null ? "—" : percent(Number.parseInt(value, 10));
}

export function Analytics() {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const [range, setRange] = useState(defaultWindow);
  // SWEEP S19. The scorecard's own client filter; the per-client table already
  // names every client with a post in the period, so it supplies the choices.
  const [clientFilter, setClientFilter] = useState("");

  const [metrics, setMetrics] = useState<ScorecardMetric[] | null>(null);
  // "Try again" re-runs every read below; each keeps its status (design/LoadFailed).
  const [reloads, setReloads] = useState(0);
  const retry = () => setReloads((n) => n + 1);
  const [metricsFailed, setMetricsFailed] = useState<Failure | null>(null);
  const [clients, setClients] = useState<ClientDelivery[] | null>(null);
  const [clientsFailed, setClientsFailed] = useState<Failure | null>(null);
  const [checks, setChecks] = useState<ReconciliationCheck[] | null>(null);
  const [reconciled, setReconciled] = useState<boolean | null>(null);
  const [checksFailed, setChecksFailed] = useState<Failure | null>(null);
  const [counters, setCounters] = useState<CountersReply | null>(null);
  const [countersFailed, setCountersFailed] = useState<Failure | null>(null);
  // A reader who may not see the scorecard is told once, at the top, not again in every panel.
  const limited = metricsFailed?.status === 403;

  useEffect(() => {
    void (async () => {
      const res = await send<ScorecardReply>(
        analyticsApi.scorecard(apiBase, range.from, range.to, clientFilter || null),
      );
      if (res.status === 200 && Array.isArray(res.body?.metrics)) {
        setMetrics(res.body.metrics);
        setMetricsFailed(null);
      } else {
        setMetrics(null);
        setMetricsFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, range, clientFilter, reloads]);

  useEffect(() => {
    void (async () => {
      const res = await send<ClientsReply>(analyticsApi.clients(apiBase, range.from, range.to));
      if (res.status === 200 && Array.isArray(res.body?.clients)) {
        setClients(res.body.clients);
        setClientsFailed(null);
      } else {
        setClients(null);
        setClientsFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, range, reloads]);

  useEffect(() => {
    void (async () => {
      const res = await send<ReconciliationReply>(
        analyticsApi.reconciliation(apiBase, range.from, range.to),
      );
      if (res.status === 200 && Array.isArray(res.body?.checks)) {
        setChecks(res.body.checks);
        setReconciled(res.body.reconciled ?? null);
        setChecksFailed(null);
      } else {
        setChecks(null);
        setReconciled(null);
        setChecksFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, range, reloads]);

  useEffect(() => {
    void (async () => {
      const res = await send<CountersReply>(analyticsApi.counters(apiBase));
      if (res.status === 200 && Array.isArray(res.body?.register)) {
        setCounters(res.body);
        setCountersFailed(null);
      } else {
        setCounters(null);
        setCountersFailed({ status: res.status, refusal: refusal(res.body?.error) });
      }
    })();
  }, [apiBase, reloads]);

  const failures = checks === null ? [] : checks.filter((c) => !c.reconciles);

  return (
    <div className="stack">
      <h1>{t("Delivery scorecard")}</h1>
      <p className="muted">
        {t("How this agency delivered over the chosen period, and whether each figure checks out against its second source. Nothing here is a stored total.")}
      </p>

      <PeriodForm range={range} onChange={setRange} />

      {reconciled === false && failures.length > 0 ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("{n} did not reconcile", { n: failures.length })} />
        </div>
      ) : null}

      <Tabs
        label={t("Delivery scorecard")}
        tabs={[
            {
              id: "perf-metrics",
              label: t("The five metrics"),
              content: (
                <>
      {metricsFailed !== null ? (
        <section className="panel">
          <LoadFailed failure={metricsFailed} fallback={t("The scorecard could not be read.")} onRetry={retry} />
        </section>
      ) : null}

      {metrics !== null ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("The five metrics")}</h2>
            {clients !== null && clients.length > 0 ? (
              <select
                aria-label={t("Client")}
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <option value="">{t("Every client")}</option>
                {[...new Map(clients.map((c) => [c.client_account_id, c.client_name]))].map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          {metrics.length === 0 ? (
            <p className="muted">
              {t("No metric is configured. That is a defect rather than an empty period — the catalogue names five.")}
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Metric")}</th>
                  <th scope="col">{t("Figure")}</th>
                  <th scope="col">{t("Check")}</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m) => (
                  <tr key={m.metric_id}>
                    <th scope="row">{tl(m.label)}</th>
                    <td className="numeric">
                      {m.is_measurable ? figure(m.journal_value, m, tl) : "—"}
                    </td>
                    <td>
                      {/* ★ NOTHING TO COMPARE IS NOT AGREEMENT. Two nulls satisfy `is not distinct from` and the database says so truthfully, but a chip reading "both sources agree" over an empty period claims a reconciliation that never happened — which is the one claim this screen exists to make carefully. Found by opening the `empty` gallery cell. */}
                      {!m.is_measurable ? (
                        <StatusChip tone="neutral" label={t("Nothing to compare")} />
                      ) : m.agrees === null ? (
                        <StatusChip tone="neutral" label={t("No live counterpart exists")} />
                      ) : m.agrees ? (
                        <StatusChip tone="live" label={t("Both sources agree")} />
                      ) : (
                        <>
                          <StatusChip tone="failed" label={t("The two sources disagree")} />
                          <p className="muted">
                            {t("Live rows {live}, journal {journal}.", { live: figure(m.live_value, m, tl), journal: figure(m.journal_value, m, tl) })}
                          </p>
                        </>
                      )}
                      {m.not_measurable_reason !== null ? (
                        <p className="muted">{tl(m.not_measurable_reason)}</p>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}

        {reconciled === false ? (
          <p className="muted">
            {t("A figure on this page is derived from two records of the same fact that no longer match. Until this is resolved, treat every rate above as unverified.")}
          </p>
        ) : null}
                </>
              ),
            },
            {
              id: "perf-recon",
              label: t("Reconciliation against the audit events"),
              content: (
                <>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Reconciliation against the audit events")}</h2>
        </div>
        {checksFailed !== null ? (
          <>
            {limited ? null : <LoadFailed failure={checksFailed} fallback={t("The reconciliation could not be read.")} onRetry={retry} />}
          </>
        ) : checks === null ? (
          <p className="muted">{t("Reading.")}</p>
        ) : failures.length === 0 ? (
          <>
            <StatusChip tone="live" label={t("Every check reconciles")} />
            <p className="muted">
              {t("{n} check(s) ran: each metric against its second source, and every state change in the journal against the audit event written in the same transaction. Nothing disagreed.", { n: checks.length })}
            </p>
          </>
        ) : (
          <>
            <StatusChip tone="failed" label={t("{n} did not reconcile", { n: failures.length })} />
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Compares")}</th>
                  <th scope="col">{t("Subject")}</th>
                  <th scope="col">{t("The first says")}</th>
                  <th scope="col">{t("The second says")}</th>
                </tr>
              </thead>
              <tbody>
                {failures.map((c) => (
                  <tr key={`${c.check_id}-${c.subject}`}>
                    <td>{c.compares}</td>
                    <td>{c.subject}</td>
                    <td>{c.first_says}</td>
                    <td>{c.second_says}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
                </>
              ),
            },
            {
              id: "perf-clients",
              label: t("Per-client delivery, by month"),
              content: (
                <>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Per-client delivery, by month")}</h2>
        </div>
        {clientsFailed !== null ? (
          <>
            {limited ? null : <LoadFailed failure={clientsFailed} fallback={t("The per-client breakdown could not be read.")} onRetry={retry} />}
          </>
        ) : clients === null ? (
          <p className="muted">{t("Reading.")}</p>
        ) : clients.length === 0 ? (
          <p className="muted">
            {t("No client had a post in this period. That is not a delivery quality of zero.")}
          </p>
        ) : (
          <>
          <button
            className="btn btn-quiet"
            type="button"
            onClick={() =>
              download(
                `delivery-by-client-${range.from.slice(0, 10)}-${new Date(Date.parse(range.to) - 1).toISOString().slice(0, 10)}.csv`,
                csv([
                  [t("Client"), t("Month"), t("Seats"), t("Filled (live)"), t("Filled (journal)"), t("Fill %"), t("Ended"), t("Absences"), t("No-show %"), t("Sources agree")],
                  ...clients.map((c) => [
                    c.client_name,
                    c.month_start,
                    c.seats,
                    c.filled_live,
                    c.filled_journal,
                    c.fill_basis_points === null ? "" : (Number(c.fill_basis_points) / 100).toFixed(2),
                    c.ended,
                    c.absences,
                    c.no_show_basis_points === null ? "" : (Number(c.no_show_basis_points) / 100).toFixed(2),
                    c.agrees ? "yes" : "no",
                  ]),
                ]),
                "text/csv",
              )
            }
          >
            {t("Download as CSV")}
          </button>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Client")}</th>
                <th scope="col">{t("Month")}</th>
                <th scope="col">{t("Seats")}</th>
                <th scope="col">{t("Fill")}</th>
                <th scope="col">{t("No-show")}</th>
                <th scope="col">{t("Sources")}</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={`${c.client_account_id}-${c.month_start}`}>
                  <th scope="row">{c.client_name}</th>
                  <td>{monthLabel(c.month_start)}</td>
                  <td className="numeric">{c.seats}</td>
                  <td className="numeric">{basisPoints(c.fill_basis_points)}</td>
                  <td className="numeric">{basisPoints(c.no_show_basis_points)}</td>
                  <td>
                    {c.agrees ? (
                      <StatusChip tone="live" label={t("Both sources agree")} />
                    ) : (
                      <StatusChip tone="failed" label={t("The two sources disagree")} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </section>
                </>
              ),
            },
            {
              id: "perf-counts",
              label: t("Stored counts"),
              content: (
                <>
      <section className="panel">
        <details open={(counters?.defects ?? []).length > 0}>
        <summary>{t("Stored counts, and why no metric reads them")}</summary>
        {countersFailed !== null ? (
          <>
            {limited ? null : <LoadFailed failure={countersFailed} fallback={t("The counter register could not be read.")} onRetry={retry} />}
          </>
        ) : counters === null ? (
          <p className="muted">{t("Reading.")}</p>
        ) : (
          <>
            {(counters.defects ?? []).length > 0 ? (
              <>
                <StatusChip tone="failed" label={t("A metric reads a stored count")} />
                <ul className="plain-list">
                  {(counters.defects ?? []).map((d) => (
                    <li key={d.system_id}>
                      {d.system_id}: {d.defect}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <StatusChip tone="live" label={t("No metric reads a stored count")} />
            )}
            {(counters.drift ?? []).length > 0 ? (
              <>
                <StatusChip tone="attention" label={t("A stored count has drifted")} />
                <ul className="plain-list">
                  {(counters.drift ?? []).map((d) => (
                    <li key={`${d.relation}-${d.column_name}-${d.subject_id}`}>
                      {t("{field} on {subject} says {stored} and there are {live}.", {
                        field: `${d.relation}.${d.column_name}`,
                        subject: d.subject_id,
                        stored: d.stored,
                        live: d.live,
                      })}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Column")}</th>
                  <th scope="col">{t("A metric may read it")}</th>
                  <th scope="col">{t("Why")}</th>
                </tr>
              </thead>
              <tbody>
                {(counters.register ?? []).map((r) => (
                  <tr key={`${r.relation}-${r.column_name}`}>
                    <th scope="row">
                      {r.relation}.{r.column_name}
                    </th>
                    <td>
                      {r.is_metric_safe ? (
                        <StatusChip tone="neutral" label={t("Safe to display")} />
                      ) : (
                        <StatusChip tone="attention" label={t("Can drift")} />
                      )}
                    </td>
                    <td>{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        </details>
      </section>
                </>
              ),
            },
        ]}
      />
    </div>
  );
}

/** SWEEP S19. The period, as two calendar days; "to" includes its whole day. */
function PeriodForm({
  range,
  onChange,
}: {
  range: { from: string; to: string };
  onChange: (r: { from: string; to: string }) => void;
}) {
  const { t } = useT();
  const [from, setFrom] = useState(range.from.slice(0, 10));
  const [to, setTo] = useState(range.to.slice(0, 10));
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        const end = new Date(`${to}T00:00:00Z`);
        end.setUTCDate(end.getUTCDate() + 1);
        onChange({ from: new Date(`${from}T00:00:00Z`).toISOString(), to: end.toISOString() });
      }}
    >
      <div className="field">
        <label htmlFor="analytics-from">{t("From")}</label>
        <input id="analytics-from" type="date" required value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="analytics-to">{t("To")}</label>
        <input id="analytics-to" type="date" required value={to} min={from} onChange={(e) => setTo(e.target.value)} />
      </div>
      <button className="btn" type="submit">
        {t("Show this period")}
      </button>
    </form>
  );
}
